import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { 
  Settings, 
  Palette, 
  Layers, 
  Users, 
  CreditCard, 
  ChevronRight,
  ShieldCheck,
  Type,
  Download,
  TrendingUp,
  Wallet
} from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';

const SettingCard: React.FC<{
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}> = ({ to, icon, title, description }) => (
  <Link to={to} className="block group">
    <Card className="hover:border-blue-300 transition-all hover:shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-50 text-blue-700 rounded-none group-hover:bg-blue-100 transition-colors">
            {icon}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">{title}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>
          </div>
        </div>
        <ChevronRight size={20} className="text-gray-300 group-hover:text-blue-500 transition-colors" />
      </div>
    </Card>
  </Link>
);

export const SettingsPage = () => {
  const { role, can } = usePermissions();
  
  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <div>
        <h1 className="text-lg md:text-2xl font-semibold text-gray-900 dark:text-white">Settings</h1>
        <p className="text-xs md:text-sm text-gray-500">Manage your organization's configuration and branding.</p>
        <div className="mt-2 flex items-center gap-2">
          <Badge variant="blue" className="text-[10px] uppercase font-mono tracking-wider">
            Effective Role: {role}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {can('edit_settings') && (
          <>
            <SettingCard 
              to="/settings/modules"
              icon={<Layers size={24} />}
              title="Modules"
              description="Enable or disable features like Chat, Ledger, and Goals."
            />
            <SettingCard 
              to="/settings/branding"
              icon={<Palette size={24} />}
              title="Branding"
              description="Customize colors, logos, and subdomain appearance."
            />
            <SettingCard 
              to="/settings/terminology"
              icon={<Type size={24} />}
              title="Governance & Terminology"
              description="Customize module names and organizational hierarchy policies."
            />
            <SettingCard 
              to="/settings/members"
              icon={<Users size={24} />}
              title="Members & Roles"
              description="Manage permissions and custom organizational roles."
            />
            <SettingCard 
              to="/settings/billing"
              icon={<TrendingUp size={24} />}
              title="Billing Plan"
              description="Manage your SaaS subscription and tier."
            />
            <SettingCard 
              to="/settings/payouts"
              icon={<Wallet size={24} />}
              title="Payment Processing"
              description="Connect Stripe, route payouts, and manage fees."
            />
            <SettingCard 
              to="/settings/data"
              icon={<Download size={24} />}
              title="Data & Compliance"
              description="Export your organization's data or manage data retention policies."
            />
          </>
        )}
      </div>

      <Card className="bg-white border-dashed border-black dark:border-white dark:bg-slate-950">
        <div className="flex items-center gap-3">
          <ShieldCheck size={20} className="text-teal-600" />
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">Permission Context</p>
            <p className="text-xs text-gray-500">
              You are seeing these settings because you are a <strong>{role}</strong>. 
              {role !== 'MEMBER' && role !== 'GUEST' ? ' Your role grants you specific capabilities defined by your organization.' : ''}
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};
